import { useState, useEffect } from 'react';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export default function SigilPlaceholder() {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state

  // Load saved image from Supabase on component mount
  useEffect(() => {
    loadCoalescenceSigil();
  }, []);

  const loadCoalescenceSigil = async () => {
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/upload-coalescence-sigil`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.data && result.data.image_url) {
          setUploadedImage(result.data.image_url);
        }
      }
    } catch (error) {
      console.error('Error loading coalescence sigil:', error);
    } finally {
      setIsLoading(false); // Set loading to false after attempt
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${SUPABASE_URL}/functions/v1/upload-coalescence-sigil`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setUploadedImage(result.data.image_url);
      } else {
        console.error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading sigil:', error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="relative">
      {/* Outer glow rings */}
      <div className="absolute inset-0 animate-pulse">
        <div className="w-80 h-80 rounded-full border border-violet-500/30 animate-ping"></div>
        <div className="absolute inset-4 rounded-full border border-cyan-400/20 animate-pulse"></div>
        <div className="absolute inset-8 rounded-full border border-violet-300/40"></div>
      </div>
      
      {/* Radiating lines */}
      <div className="absolute inset-0 animate-spin" style={{ animationDuration: '20s' }}>
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute w-px h-20 bg-gradient-to-t from-transparent via-violet-400/50 to-transparent"
            style={{
              top: '50%',
              left: '50%',
              transformOrigin: '0 0',
              transform: `rotate(${i * 45}deg) translateY(-160px)`,
            }}
          />
        ))}
      </div>
      
      {/* Main sigil container */}
      <div className="relative w-64 h-64 rounded-full bg-gradient-to-br from-violet-900/20 to-cyan-900/20 backdrop-blur-sm border border-violet-500/50 flex items-center justify-center">
        {uploadedImage ? (
          /* Uploaded image - static display only */
          <div className="relative w-48 h-48 rounded-full overflow-hidden">
            <img 
              src={uploadedImage} 
              alt="Coalescence Sigil" 
              className="w-full h-full object-cover"
              onError={() => {
                console.error('Image failed to load');
                setUploadedImage(null);
              }}
            />
          </div>
        ) : (
          /* Geometric placeholder */
          <div className="relative">
            {/* Central diamond */}
            <div className="w-16 h-16 bg-gradient-to-br from-violet-400 to-cyan-400 transform rotate-45 opacity-80"></div>
            
            {/* Surrounding elements */}
            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-violet-300 rounded-full opacity-60"></div>
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-cyan-300 rounded-full opacity-60"></div>
            <div className="absolute -left-8 top-1/2 transform -translate-y-1/2 w-4 h-4 bg-violet-300 rounded-full opacity-60"></div>
            <div className="absolute -right-8 top-1/2 transform -translate-y-1/2 w-4 h-4 bg-cyan-300 rounded-full opacity-60"></div>
            
            {/* Connecting lines */}
            <div className="absolute top-1/2 left-1/2 w-24 h-px bg-gradient-to-r from-violet-400/50 to-cyan-400/50 transform -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute top-1/2 left-1/2 w-px h-24 bg-gradient-to-b from-violet-400/50 to-cyan-400/50 transform -translate-x-1/2 -translate-y-1/2"></div>
          </div>
        )}
        
        {/* Inner glow */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-500/10 to-cyan-500/10 animate-pulse"></div>
      </div>

      {/* Upload button - only show if no image uploaded AND not loading */}
      {!uploadedImage && !isLoading && (
        <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2">
          <label className="cursor-pointer bg-violet-600/20 hover:bg-violet-600/30 border border-violet-500/50 rounded-lg px-4 py-2 text-violet-300 text-sm transition-all duration-300 backdrop-blur-sm">
            {isUploading ? 'Uploading...' : 'Upload Coalescence Sigil'}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              disabled={isUploading}
            />
          </label>
        </div>
      )}
    </div>
  );
}
